(ns ui.editor2.sidebar-giphy
  "The GIF sidebar of editor2: searches Giphy for stickers and GIFs and
   adds the chosen one as clip, or exchanges the GIF of the selected
   clip. Pure Replicant, the actions are in `ui.editor2.giphy`."
  (:require [ui.editor2.giphy :as giphy]))

(defn icon [selected?]
  [:svg.size-6
   {:xmlns "http://www.w3.org/2000/svg"
    :fill "none"
    :viewBox "0 0 24 24"
    :stroke-width "1.5"
    :stroke "currentColor"
    :class ["size-6" (when-not selected? "text-slate-400")]}
   [:path
    {:stroke-linecap "round"
     :stroke-linejoin "round"
     :d "M12.75 8.25v7.5m6-7.5h-3V12m0 0v3.75m0-3.75H18M9.75 9.348c-1.03-1.464-2.698-1.464-3.728 0-1.03 1.465-1.03 3.84 0 5.304 1.03 1.464 2.699 1.464 3.728 0V12h-1.5M4.5 19.5h15a2.25 2.25 0 0 0 2.25-2.25V6.75A2.25 2.25 0 0 0 19.5 4.5h-15a2.25 2.25 0 0 0-2.25 2.25v10.5A2.25 2.25 0 0 0 4.5 19.5Z"}]])

(defn gif-entry
  [selected?]
  [:div
   {:class ["flex" "pt-3" "pb-3" "pl-2" "pr-2" "flex-col" "justify-center" "items-center" "gap-1" "rounded-lg" "cursor-pointer" "hover:bg-gray-600"
            (when selected?
              "bg-gray-800")]
    :on {:click (cond-> [[:store/assoc-in [:editor2 :sidebar-panel] :gif-sidebar]]
                  (not selected?)
                  (conj [:store/assoc-in [:editor2 :giphy-sidebar] nil]))}}
   (icon selected?)
   [:span
    {:class ["text-center" "text-xs" "font-medium" (when-not selected? "text-slate-400")]}
    "Gif"]])

(defn outline-icon
  [d]
  [:svg {:xmlns "http://www.w3.org/2000/svg"
         :fill "none"
         :viewBox "0 0 24 24"
         :stroke-width "2"
         :stroke "currentColor"
         :aria-hidden "true"
         :class ["w-4" "h-4"]}
   [:path {:stroke-linecap "round"
           :stroke-linejoin "round"
           :d d}]])

(def search-icon
  (outline-icon "m21 21-5.197-5.197m0 0A7.5 7.5 0 1 0 5.196 5.196a7.5 7.5 0 0 0 10.607 10.607Z"))

(def clear-icon
  (outline-icon "M6 18 18 6M6 6l12 12"))

(def spinner
  [:div {:class ["animate-spin" "motion-reduce:animate-none" "rounded-full" "h-4" "w-4"
                 "border-2" "border-gray-500" "border-t-white"]}])

(def focus-ring
  ["focus:outline-none" "focus-visible:ring-2" "focus-visible:ring-blue-400"
   "focus-visible:ring-offset-2" "focus-visible:ring-offset-gray-800"])

(defn search-form
  [panel replacing?]
  (let [searching? (:searching? panel)
        search-value (:search-value panel "")]
    [:form
     {:class ["flex" "flex-col" "gap-2"]
      :role "search"
      :on {:submit [[:js/prevent-default]
                    [:editor2.giphy/search]]}}
     [:label
      {:for "giphy-search"
       :class ["text-sm" "font-medium" "text-white"]}
      (if replacing?
        "Exchange the GIF"
        "Add a GIF")]
     [:div
      {:class ["relative"]}
      [:input
       {:id "giphy-search"
        :class ["w-full" "rounded-md" "border" "border-gray-900" "text-white" "text-sm" "pl-9" "pr-9"
                "placeholder:text-gray-500" "focus:border-blue-400" "focus:ring-1" "focus:ring-blue-400"]
        :style {:background-color "oklch(0.21 0.034 264.665)"}
        ;; Not type=search: Chrome and Safari would add their own clear
        ;; button next to ours.
        :type "text"
        :enterkeyhint "search"
        :autocomplete "off"
        :placeholder "Search GIPHY"
        :value search-value
        :on {:input [[:editor2.giphy/set-search-value :event/target.value]]}}]
      [:button
       {:type "submit"
        :title "Search"
        :class (into ["absolute" "inset-y-0" "left-0" "flex" "items-center" "justify-center" "w-9"
                      "rounded-l-md" "text-gray-400" "hover:text-white" "transition-colors"]
                     focus-ring)}
       (if searching?
         spinner
         search-icon)]
      (when (seq search-value)
        [:button
         {:type "button"
          :title "Clear the search"
          :class (into ["absolute" "inset-y-0" "right-0" "flex" "items-center" "justify-center" "w-9"
                        "rounded-r-md" "text-gray-400" "hover:text-white" "transition-colors"]
                       focus-ring)
          :on {:click [[:editor2.giphy/clear-search]]}}
         clear-icon])]]))

(defn tab
  [panel mode label]
  (let [active? (= (:mode panel)
                   mode)]
    [:button
     {:type "button"
      :aria-pressed (str active?)
      ;; Active and inactive tab differ only in color, so the labels
      ;; never move:
      :class (-> ["rounded-md" "py-1.5" "text-sm" "font-medium" "text-center" "transition-colors"]
                 (into focus-ring)
                 (into (if active?
                         ["bg-gray-600" "text-white" "shadow-sm"]
                         ["text-gray-400" "hover:text-gray-100" "hover:bg-gray-700"])))
      :on {:click [[:editor2.giphy/set-mode mode]]}}
     label]))

(defn tabs
  [panel]
  [:div
   {:class ["grid" "grid-cols-2" "gap-1" "p-1" "rounded-lg" "bg-gray-900"]
    :role "group"
    :aria-label "Kind"}
   (tab panel :stickers "Stickers")
   (tab panel :gifs "GIFs")])

(defn caption
  "What the grid shows, e.g. 'Trending stickers' or 'GIFs for “cat”'."
  [panel]
  (let [gifs? (= :gifs (:mode panel))
        query (:query panel)]
    [:p
     {:class ["text-xs" "font-medium" "text-gray-400" "truncate"]}
     (if (seq query)
       (str (if gifs? "GIFs" "Stickers") " for “" query "”")
       (str "Trending " (if gifs? "GIFs" "stickers")))]))

(defn play-preview!
  "Shows the animated preview (`playing?`) or the still of the tile's
   image. Changes only the DOM: going through the store would render all
   of editor2 on every mouse move across the grid. According to
   https://caniuse.com/webp all major browsers support webp, which is
   smaller than a gif, not much bigger than an mp4 and transparent."
  [playing? ^js event]
  (when-let [img (.querySelector (.-currentTarget event) "img")]
    (set! (.-src img)
          (if playing?
            (.. img -dataset -preview)
            (.. img -dataset -still)))))

(defn gif-tile
  "A still image that plays the GIF on hover and keyboard focus."
  [index gif]
  (let [;; The index keeps the keys unique, even if Giphy returns a GIF twice:
        tile-key (str index "-" (:id gif))
        still (get-in gif [:images :downsized_still :url])]
    [:button
     {:replicant/key tile-key
      :type "button"
      :title (:title gif)
      :class (into ["aspect-square" "rounded-md" "overflow-hidden" "bg-gray-700" "p-1"
                    "flex" "items-center" "justify-center" "transition"
                    "hover:bg-gray-600" "hover:ring-2" "hover:ring-blue-400"]
                   focus-ring)
      :on {:mouseenter (partial play-preview! true)
           :mouseleave (partial play-preview! false)
           :focus (partial play-preview! true)
           :blur (partial play-preview! false)
           :click [[:editor2.giphy/select gif]]}}
     [:img
      {:class ["max-w-full" "max-h-full" "object-contain"]
       :alt (:title gif)
       :loading "lazy"
       :data-still still
       ;; Some GIFs have no webp preview, they keep the still on hover:
       :data-preview (or (get-in gif [:images :preview_webp :url])
                         still)
       :src still}]]))

(defn skeleton-tile
  [i]
  [:div
   {:replicant/key (str "skeleton-" i)
    :aria-hidden "true"
    :class ["aspect-square" "rounded-md" "bg-gray-700" "animate-pulse" "motion-reduce:animate-none"]}])

(def skeleton-count
  ;; Four rows of the three-column grid fill the visible sidebar:
  12)

(defn grid
  [& tiles]
  (into [:div {:class ["grid" "grid-cols-3" "gap-2"]}]
        tiles))

(defn results
  "The results grid. A new search or tab shows skeleton tiles in place of
   the results, \"Load more\" appends a row of skeleton tiles, so the
   panel never collapses while it loads."
  [panel]
  (let [gifs (:gifs panel)]
    (cond
      (:searching? panel)
      [:div {:aria-busy "true"}
       (apply grid (map skeleton-tile (range skeleton-count)))]

      (and (:error? panel)
           (empty? gifs))
      [:div
       {:class ["flex" "flex-col" "items-center" "gap-3" "py-10" "text-center"]}
       [:p {:class ["text-sm" "text-gray-300"]}
        "GIPHY didn't respond."]
       [:button
        {:type "button"
         :class (into ["px-3" "py-1.5" "rounded-md" "bg-gray-700" "hover:bg-gray-600" "text-sm" "text-white" "transition-colors"]
                      focus-ring)
         :on {:click [[:editor2.giphy/retry]]}}
        "Try again"]]

      (empty? gifs)
      [:p
       {:class ["py-10" "text-sm" "text-gray-400" "text-center"]}
       "Nothing found. Try another keyword."]

      :else
      [:div
       {:aria-busy (str (boolean (:loading-more? panel)))}
       (apply grid
              (concat (map-indexed gif-tile
                                   gifs)
                      (when (:loading-more? panel)
                        (map skeleton-tile (range 3)))))])))

(defn load-more
  [panel]
  (when (and (seq (:gifs panel))
             (not (:searching? panel))
             (or (:more? panel)
                 (:error? panel)))
    [:button
     {:type "button"
      :disabled (boolean (:loading-more? panel))
      :class (into ["w-full" "flex" "items-center" "justify-center" "gap-2" "py-2" "rounded-md"
                    "bg-gray-700" "hover:bg-gray-600" "text-sm" "font-medium" "text-white" "transition-colors"
                    "disabled:cursor-wait" "disabled:hover:bg-gray-700"]
                   focus-ring)
      :on {:click [[:editor2.giphy/load-more]]}}
     (cond
       (:loading-more? panel) (list spinner "Loading")
       (:error? panel) "GIPHY didn't respond. Try again"
       :else "Load more")]))

(def attribution
  ;; GIPHY requires the "Powered by GIPHY" attribution. The logo in
  ;; /img/giphy_logo.png is at most half opaque and stays dim on the dark
  ;; sidebar, therefore the attribution is set as text:
  [:a
   {:href "https://giphy.com"
    :target "_blank"
    :rel "noopener noreferrer"
    :class (into ["self-center" "rounded" "px-1" "text-xs" "text-gray-300" "hover:text-white" "transition-colors"]
                 focus-ring)}
   "Powered by "
   [:span {:class ["font-extrabold" "tracking-wider" "text-white"]}
    "GIPHY"]])

(defn panel-content
  "The search, the kind tabs, the results and \"Load more\" of the panel
   state `panel`. `replacing?` is true while a GIF clip is selected, then
   a click exchanges its GIF."
  [panel replacing?]
  [:div
   {:class ["flex" "flex-col" "gap-4" "w-full"]}
   (search-form panel replacing?)
   (tabs panel)
   [:div
    {:class ["flex" "flex-col" "gap-2"]}
    (caption panel)
    (results panel)]
   (load-more panel)
   attribution])

(defn panel
  [state]
  (let [sidebar-state (get-in state [:editor2 :giphy-sidebar])
        panel-key (or (:panel-key sidebar-state)
                      "new")
        giphy-panel (get-in state giphy/panel-path)]
    [:div
     {:replicant/key (str "gif-panel-" panel-key)
      ;; Each mount starts with the remembered search of the GIF clip (or
      ;; the trending stickers) and loads its first page:
      :replicant/on-mount [[:editor2.giphy/init
                            panel-key
                            (:initial-state sidebar-state)]]
      ;; Without its state, a reopened panel starts with skeleton tiles
      ;; instead of a frame of the previous results:
      :replicant/on-unmount [[:editor2.giphy/unmount panel-key]]
      :class ["flex" "flex-col" "w-full"]}
     (panel-content (if (= panel-key (:panel-key giphy-panel))
                      giphy-panel
                      (assoc giphy/default-panel-state
                             :searching? true))
                    (some? (:clip-uuid sidebar-state)))]))
